import React, { useState, useEffect, useContext } from 'react';
import { AuthContext, API } from '../../App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import { Settings, Shield, Lock, Save, Eye, EyeOff } from 'lucide-react';

const settingsMenu = [
  { id: 'security', label: 'Güvenlik', icon: Shield, description: 'Kullanıcı adı, şifre ve ultra password' },
];

export default function AdminSettings() {
  const { user } = useContext(AuthContext);
  const isMasterAdmin = user?.email === 'mbucak@gmail.com';
  const [selectedMenu, setSelectedMenu] = useState('security');
  const [settings, setSettings] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    newPassword: '',
    confirmPassword: '',
    ultraPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pwdTouched, setPwdTouched] = useState(false);
  const [confirmTouched, setConfirmTouched] = useState(false);
  const [pwdError, setPwdError] = useState('');

  const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[%&?!@#$^*]).{6,}$/;
  const validatePwd = (v) => {
    if (!v) return '';
    if (!PASSWORD_REGEX.test(v)) return 'Şifre kurallara uymuyor';
    return '';
  };

  const confirmError = (() => {
    const { newPassword, confirmPassword } = formData;
    if (!confirmPassword) return '';
    if ((confirmTouched || confirmPassword.length >= newPassword.length) && confirmPassword !== newPassword)
      return 'Şifreler eşleşmiyor';
    return '';
  })();

  useEffect(() => {
    fetchSettings();
    if (user) {
      setFormData(prev => ({ ...prev, email: user.email }));
    }
  }, [user]);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(res.data);
      setFormData(prev => ({ ...prev, ultraPassword: res.data.ultra_password }));
      setLoading(false);
    } catch (error) {
      toast.error('Ayarlar yüklenemedi');
      setLoading(false);
    }
  };

  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/auth/update-profile?email=${formData.email}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Kullanıcı adı güncellendi');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Güncelleme başarısız');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    const err = validatePwd(formData.newPassword);
    if (err) { setPwdError(err); setPwdTouched(true); return; }
    if (confirmError || formData.newPassword !== formData.confirmPassword) {
      toast.error('Şifreler eşleşmiyor');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/auth/change-password-direct?new_password=${formData.newPassword}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Şifre değiştirildi');
      setFormData(prev => ({ ...prev, newPassword: '', confirmPassword: '' }));
      setPwdTouched(false); setConfirmTouched(false); setPwdError('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Şifre değiştirilemedi');
    }
  };

  const handleUpdateUltraPassword = async (e) => {
    e.preventDefault();
    
    if (!/^\d{6}$/.test(formData.ultraPassword)) {
      toast.error('Ultra Password 6 haneli rakam olmalı');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/settings`, {
        ultra_password: formData.ultraPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ultra Password güncellendi');
      fetchSettings();
    } catch (error) {
      toast.error('Ultra Password güncellenemedi');
    }
  };

  return (
    <div className="p-8" data-testid="admin-settings">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Ayarlar</h1>
        <p className="text-gray-500 mt-1">Sistem ayarlarını yönet</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Settings Menu */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Ayarlar Menüsü
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[600px]">
              <div className="space-y-2">
                {settingsMenu.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      onClick={() => setSelectedMenu(item.id)}
                      className={`w-full text-left p-3 rounded-lg border transition-colors ${
                        selectedMenu === item.id
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                      data-testid={`settings-menu-${item.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="w-5 h-5" />
                        <div>
                          <div className="font-medium">{item.label}</div>
                          <div className="text-xs text-gray-500">{item.description}</div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Settings Content */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>
              {settingsMenu.find(m => m.id === selectedMenu)?.label}
            </CardTitle>
            <CardDescription>
              {settingsMenu.find(m => m.id === selectedMenu)?.description}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {selectedMenu === 'security' && (
              <div className="space-y-8">
                {/* Email - readonly, cannot be changed */}
                <div className="border-b pb-6">
                  <h3 className="text-lg font-semibold mb-4">Kullanıcı Adı (E-posta)</h3>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">E-posta Adresi</label>
                    <input
                      type="email"
                      value={formData.email}
                      disabled
                      className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-100 text-gray-400 cursor-not-allowed text-sm"
                    />
                    <p className="text-xs text-gray-400">Ultra admin e-posta adresi değiştirilemez.</p>
                  </div>
                </div>

                {/* Password Change - only for mbucak@gmail.com */}
                {isMasterAdmin && (
                <div className="border-b pb-6">
                  <h3 className="text-lg font-semibold mb-4">Şifre Değiştir</h3>
                  <form onSubmit={handleChangePassword} className="space-y-4">
                    <div className="space-y-1.5">
                      <Label>Yeni Şifre</Label>
                      <div className="relative">
                        <Input
                          type={showPassword ? "text" : "password"}
                          value={formData.newPassword}
                          onChange={(e) => {
                            setFormData({...formData, newPassword: e.target.value});
                            if (pwdTouched) setPwdError(validatePwd(e.target.value));
                          }}
                          onBlur={() => { setPwdTouched(true); setPwdError(validatePwd(formData.newPassword)); }}
                          data-testid="new-password-input"
                          className={`pr-10 ${pwdError ? 'border-red-400' : ''}`}
                        />
                        <button type="button" onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700">
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      {pwdError && <p className="text-xs text-red-500">{pwdError}</p>}
                      <p className="text-[11px] text-gray-400">En az 6 karakter, 1 büyük harf, 1 küçük harf ve 1 özel karakter (%&?!@#) içermelidir.</p>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Yeni Şifre (Tekrar)</Label>
                      <div className="relative">
                        <Input
                          type={showConfirmPassword ? "text" : "password"}
                          value={formData.confirmPassword}
                          onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                          onBlur={() => setConfirmTouched(true)}
                          data-testid="confirm-password-input"
                          className={`pr-10 ${confirmError ? 'border-red-400' : ''}`}
                        />
                        <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700">
                          {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      {confirmError && <p className="text-xs text-red-500">{confirmError}</p>}
                    </div>
                    <Button type="submit" data-testid="change-password-btn">
                      <Lock className="w-4 h-4 mr-2" />
                      Şifre Değiştir
                    </Button>
                  </form>
                </div>
                )} {/* end isMasterAdmin password */}

                {/* Super Password - only for mbucak@gmail.com */}
                {isMasterAdmin ? (
                <div>
                  <h3 className="text-lg font-semibold mb-2">Süper Şifre</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Kullanıcı silme işlemlerinde doğrulama için kullanılır. Sadece siz belirleyebilirsiniz.
                  </p>
                  <form onSubmit={handleUpdateUltraPassword} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Süper Şifre (en az 4 karakter)</Label>
                      <Input
                        type="text"
                        value={formData.ultraPassword}
                        onChange={(e) => setFormData({...formData, ultraPassword: e.target.value})}
                        placeholder="Süper şifrenizi girin"
                        data-testid="ultra-password-input"
                      />
                      <p className="text-xs text-gray-500">
                        Mevcut süper şifre: {settings?.ultra_password ? '••••••' : 'Henüz belirlenmemiş (varsayılan: 123456)'}
                      </p>
                    </div>
                    <Button type="submit" data-testid="update-ultra-password-btn">
                      <Shield className="w-4 h-4 mr-2" />
                      Süper Şifreyi Kaydet
                    </Button>
                  </form>
                </div>
                ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-500">
                  Süper şifre yönetimi yalnızca sistem yöneticisi tarafından yapılabilir.
                </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}